import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import type { ClientProfile, PlanData, SubscriptionData, PaymentRecord, UsageStats } from './types';
import ClientHeader from './components/ClientHeader';
import OverviewTab from './components/OverviewTab';
import PersonalTab from './components/PersonalTab';
import SubscriptionTab from './components/SubscriptionTab';
import PaymentsTab from './components/PaymentsTab';
import MarketingTab from './components/MarketingTab';
import TimelineTab from './components/TimelineTab';

type Tab = 'overview' | 'personal' | 'subscription' | 'payments' | 'marketing' | 'timeline';

export interface AuditLogRecord {
  id: string;
  actor_id: string;
  action: string;
  resource: string;
  resource_id: string | null;
  target_type: string | null;
  target_id: string | null;
  details: Record<string, unknown> | string | null;
  created_at: string;
}

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: 'overview', label: 'Visão Geral', icon: 'ri-dashboard-line' },
  { id: 'personal', label: 'Dados Pessoais', icon: 'ri-user-line' },
  { id: 'subscription', label: 'Plano & Assinatura', icon: 'ri-vip-crown-line' },
  { id: 'payments', label: 'Pagamentos', icon: 'ri-bank-card-line' },
  { id: 'marketing', label: 'CRM & Marketing', icon: 'ri-megaphone-line' },
  { id: 'timeline', label: 'Histórico', icon: 'ri-history-line' },
];

const ClientDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('overview');
  const [profile, setProfile] = useState<ClientProfile | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [plans, setPlans] = useState<PlanData[]>([]);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [usage, setUsage] = useState<UsageStats | null>(null);
  const [auditLogs, setAuditLogs] = useState<AuditLogRecord[]>([]);
  const [loading, setLoading] = useState(true);

  const loadClient = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { data: prof } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (!prof) {
        navigate('/admin/usuarios');
        return;
      }
      setProfile(prof as ClientProfile);

      const { data: sub } = await supabase
        .from('subscriptions')
        .select('id, plan_id, status, subscription_type, amount, billing_cycle, started_at, expires_at, auto_renew, plans(id, name, slug)')
        .eq('user_id', id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (sub) {
        const p = (sub as any).plans;
        setSubscription({
          id: sub.id,
          plan_id: sub.plan_id,
          plan_name: p?.name || '—',
          plan_slug: p?.slug || '—',
          status: sub.status,
          subscription_type: sub.subscription_type,
          amount: sub.amount,
          billing_cycle: sub.billing_cycle,
          started_at: sub.started_at,
          expires_at: sub.expires_at,
          auto_renew: sub.auto_renew,
        });
      }

      const { data: plansData } = await supabase
        .from('plans')
        .select('*')
        .eq('is_active', true)
        .order('price_brl', { ascending: true });
      setPlans((plansData || []) as PlanData[]);

      const { data: pays } = await supabase
        .from('payments')
        .select('id, amount, status, payment_method, gateway, gateway_transaction_id, paid_at, created_at, notes, gateway_response, plans(name)')
        .eq('user_id', id)
        .order('created_at', { ascending: false })
        .limit(50);
      setPayments(
        ((pays || []) as any[]).map((p) => ({ ...p, plan_name: p.plans?.name || null }))
      );

      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];

      const [accRes, cardRes, goalRes, txRes, lastTxRes, auditRes] = await Promise.all([
        supabase.from('accounts').select('*', { count: 'exact', head: true }).eq('user_id', id),
        supabase.from('cards').select('*', { count: 'exact', head: true }).eq('user_id', id),
        supabase.from('goals').select('*', { count: 'exact', head: true }).eq('user_id', id),
        supabase.from('transactions').select('*', { count: 'exact', head: true })
          .eq('user_id', id).gte('transaction_date', startOfMonth).lte('transaction_date', endOfMonth),
        supabase.from('transactions').select('transaction_date').eq('user_id', id)
          .order('transaction_date', { ascending: false }).limit(1).maybeSingle(),
        supabase.from('audit_logs')
          .select('id, actor_id, action, resource, resource_id, target_type, target_id, details, created_at')
          .eq('actor_id', id)
          .order('created_at', { ascending: false })
          .limit(100),
      ]);

      setUsage({
        accounts: accRes.count ?? 0,
        cards: cardRes.count ?? 0,
        goals: goalRes.count ?? 0,
        monthly_transactions: txRes.count ?? 0,
        last_transaction_date: (lastTxRes.data as any)?.transaction_date || null,
      });

      setAuditLogs((auditRes.data || []) as AuditLogRecord[]);
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadClient();
  }, [loadClient]);

  const updateProfile = (updated: Partial<ClientProfile>) => {
    if (!profile) return;
    setProfile({ ...profile, ...updated });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <i className="ri-loader-4-line text-3xl text-violet-400 animate-spin" />
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div>
      {/* Back */}
      <button
        onClick={() => navigate('/admin/usuarios')}
        className="flex items-center gap-2 text-[13px] text-white/40 hover:text-white mb-5 transition-colors cursor-pointer whitespace-nowrap"
      >
        <i className="ri-arrow-left-line" />
        Voltar para Usuários
      </button>

      {/* Header */}
      <ClientHeader profile={profile} subscription={subscription} onUpdate={updateProfile} />

      {/* Tabs */}
      <div className="flex items-center gap-0 mt-6 border-b border-white/10 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-3 text-[13px] font-500 border-b-2 -mb-px transition-all cursor-pointer whitespace-nowrap ${
              tab === t.id
                ? 'border-violet-500 text-violet-300'
                : 'border-transparent text-white/40 hover:text-white/70'
            }`}
          >
            <i className={t.icon} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="mt-6">
        {tab === 'overview' && (
          <OverviewTab profile={profile} subscription={subscription} usage={usage} plans={plans} />
        )}
        {tab === 'personal' && (
          <PersonalTab profile={profile} onUpdate={updateProfile} />
        )}
        {tab === 'subscription' && (
          <SubscriptionTab profile={profile} subscription={subscription} plans={plans} onUpdate={updateProfile} onRefresh={loadClient} />
        )}
        {tab === 'payments' && (
          <PaymentsTab payments={payments} />
        )}
        {tab === 'marketing' && (
          <MarketingTab profile={profile} onUpdate={updateProfile} />
        )}
        {tab === 'timeline' && (
          <TimelineTab profile={profile} payments={payments} auditLogs={auditLogs} />
        )}
      </div>
    </div>
  );
};

export default ClientDetailPage;
